import { AreaSelectProps, CountryPhoneInput, CountryPhoneInputProps } from 'antd-country-phone-input'
import cx from 'classnames'
import levenshtein from 'js-levenshtein'
import { isFinite } from 'lodash'
import React from 'react'
import { AsYouType, CountryCode, examples, getExampleNumber } from '@commutifi-fe/phone-number'
import { EmptyDropdown } from '../EmptyDropdown'

export type PhoneInputValue = CountryPhoneInputProps['value']

export type AppPhoneInputProps = CountryPhoneInputProps

// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents -- Legacy
export const getValueVsTemplateLength = (number: string, countryCode: string | CountryCode | undefined) => {
  const asYouType = new AsYouType(countryCode as CountryCode)
  const ex = getExampleNumber(countryCode as CountryCode, examples)
  const nationalNumber = ex?.formatNational() || ''
  asYouType.input(nationalNumber)

  const template = asYouType.getTemplate()
  const templateLength = (template.match(/x/g) || []).length
  const value = new AsYouType(countryCode as CountryCode)
  value.input(number)
  return {
    currentValueLength: value.getChars().length,
    templateLength,
    template
  }
}

export function PhoneInput({ onChange, value, placeholder, ...props }: AppPhoneInputProps) {
  const [searchValue, setSearchValue] = React.useState<string>('')
  const [controlledValue, setControlledValue] = React.useState<PhoneInputValue>(
    value?.phone
      ? {
          ...value,
          phone: new AsYouType(value.short as CountryCode | undefined).input(value.phone)
        }
      : value
  )
  // const [previousValue, setValue] = React.useState<PhoneInputValue>()
  const handleOnChange: CountryPhoneInputProps['onChange'] = (phoneValue) => {
    if (!onChange) {
      return phoneValue
    }

    // When we erase characters we know the value was already formatted so we simply keep the value as is.
    // Also, check that length wasn't too long cause then the formatting was lost
    const { currentValueLength: previousValueLength, templateLength } = getValueVsTemplateLength(
      controlledValue?.phone || '',
      phoneValue.short
    )
    if (
      !phoneValue.phone ||
      (controlledValue?.phone &&
        phoneValue.phone.length < controlledValue.phone.length &&
        previousValueLength <= templateLength)
    ) {
      setControlledValue(phoneValue)
      onChange(phoneValue)
      return undefined
    }

    const newValue = {
      ...phoneValue,
      phone: new AsYouType(phoneValue.short as CountryCode | undefined).input(phoneValue.phone)
    }
    setControlledValue(newValue)
    onChange(newValue)
    return undefined
  }

  const selectProps = React.useMemo<AreaSelectProps>(
    () => ({
      'aria-label': 'Country Code',
      className: cx(controlledValue?.code && controlledValue.phone && 'ant-select-status-success', 'app-select'),
      searchValue,
      onSearch: (value) => {
        setSearchValue(value)
      },
      filterOption: (input, option) =>
        // Note: option.key looks like 'Canada 1' or 'United States 1' or 'United Kingdom 44', etc.
        // Here we simple try to match a substring search value with each country to filter matches
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unnecessary-condition -- Legacy
        option?.key?.toLowerCase().indexOf((input || '')?.toLowerCase()) >= 0,
      notFoundContent: <EmptyDropdown className="u-padding--medium" />,
      filterSort: (optionA, optionB) => {
        if (!searchValue || searchValue.trim() === '') {
          // Avoid levenshtein calculation when there is no search value since it can be expensive
          // and there is nothing to compare so there is no value in comparing the options
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access -- Legacy
          return optionA.key.localeCompare(optionB.key)
        }

        // Note: option.key looks like 'Canada 1' or 'United States 1' or 'United Kingdom 44', etc.
        const isNumberSearchOnly =
          isFinite(parseInt(searchValue)) && `${parseInt(searchValue)}`.length === searchValue.trim().length
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Legacy
        const compareA = ((isNumberSearchOnly ? optionA.key.split(' ').pop() : optionA.key) || '').toLowerCase()
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Legacy
        const compareB = ((isNumberSearchOnly ? optionB.key.split(' ').pop() : optionB.key) || '').toLowerCase()

        // levenshtein algo basically gives a number which represents the minimum change of letters + substitution, etc. to obtain the target string.
        // So the least changes needed to match the search with a country string the better it matches
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- Legacy
        return levenshtein(searchValue, compareA) > levenshtein(searchValue, compareB) ? 1 : -1
      }
    }),
    [controlledValue?.code, controlledValue?.phone, searchValue]
  )

  return (
    <CountryPhoneInput
      inline
      className={cx('app-input')}
      onChange={handleOnChange}
      value={controlledValue}
      placeholder={placeholder || getExampleNumber(value?.short as CountryCode, examples)?.formatNational()}
      selectProps={selectProps}
      {...props}
    />
  )
}

export default PhoneInput
